import React, { useContext, useEffect, useRef, useState } from 'react';
import Image from 'next/future/image';
import cn from 'classnames';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { CtaButton } from '@/components/Cta/Cta';
import useCollectionPageScrollEvent from '../useCollectionPageScrollEvent';
import { Variants } from '@/lib/search-helpers';
import { useMediaQuery } from '@/components/commons/hooks';
import { Breakpoints } from '@/settings/breakpoints';
import i18n from '@/settings/i18n';
import filterIcon from '@/public/icons/collection/filter.svg';
import { DealsResults, SPOTLIGHT_DEALS_COUNT } from './types';
import FilterSidebar from './FilterSidebar';
import { QsContext, useQs } from '@/lib/use-qs';
import FilterBy from './FilterBy';
import { isPriceRangeFilterChanged } from './helpers';
import { trackProductListViewed } from '@/lib/analytics/trackProduct';
import Spotlight from './Spotlight';
import useWindowSize from '@/lib/use-window-size';
import CategoryFilter from './CategoryFilter';
import SortSelect from './SortSelect';

const InfiniteScrollSearchResults = dynamic(
  () => import('./InfiniteScrollSearchResults'),
  {
    ssr: false,
  },
);

const t = i18n.init();

export default function HotDealsLayout(props: DealsResults) {
  const router = useRouter();
  const { query } = router.query;
  const { deals, hasNextPage } = props;
  const [qs, updateQs] = useQs<string>();
  const [qry] = useContext(QsContext);
  const [showFilter, setFilterIsOpen] = useState(false);
  const [dropdownOpen, setDropDownOpen] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const isTabletMobile = useMediaQuery(Breakpoints.LG);
  const { shouldShowTopBar, isScrolling } = useCollectionPageScrollEvent();
  const [screenWidth] = useWindowSize();

  useEffect(() => {
    trackProductListViewed(deals);
  }, [qs]);

  const onPriceRangeChange = ({ min, max }: { min: number; max: number }) => {
    // we remove the min/max the url if it is out of bound
    const minPrice = min <= props.priceRange.min ? undefined : min.toString();
    const maxPrice = props.priceRange.max <= max ? undefined : max.toString();

    updateQs({
      minPrice,
      maxPrice,
    });
  };

  const allowedFilters = [
    'categories',
    'brand',
    'minPrice',
    'maxPrice',
    'dealTypes',
  ];

  const flattenSelectedVariants: Array<{
    variant: string;
    variantGroup: Variants;
  }> = Object.keys(qry)
    .filter(
      (key) =>
        key !== 'minPrice' &&
        key !== 'maxPrice' &&
        allowedFilters.includes(key),
    )
    .flatMap((filter) => {
      const selectedFields = qry[filter as Variants] ?? [];

      return selectedFields.split('|').map((selectedField: any) => ({
        variant: selectedField,
        variantGroup: filter as Variants,
      }));
    });

  const priceRange = {
    min: Number(qs.minPrice ?? 0).toFixed(2),
    max: Number(qs.maxPrice ?? 0).toFixed(2),
  };

  const isPriceRangeChanged = isPriceRangeFilterChanged(
    priceRange,
    props.priceRange,
  );

  const numberOfFilters =
    flattenSelectedVariants.length + (isPriceRangeChanged ? 1 : 0);

  const handleDropdownOpen = (toggle: boolean) => {
    if (toggle) setDropDownOpen((prevState) => !prevState);
    else setDropDownOpen(false);
  };

  const handleDropdownCloseOnScroll = () => {
    if (isTabletMobile) setDropDownOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      event.target &&
      !dropdownRef.current.textContent?.includes(
        (event.target as HTMLDivElement).textContent?.substring(0, 50) ?? '',
      )
    )
      setDropDownOpen(false);
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('scroll', handleDropdownCloseOnScroll, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('scroll', handleDropdownCloseOnScroll, true);
    };
  }, [dropdownOpen]);

  const onPriceRangeRemove = () =>
    updateQs({
      minPrice: undefined,
      maxPrice: undefined,
    });

  // We need two more deals if the screen width is between 767 and 1280 to fill up the gap created by the grid.
  const spotlightDeals =
    screenWidth < 1280 && screenWidth > 767
      ? props.spotlightDeals?.slice(0, 11)
      : props.spotlightDeals?.slice(0, 9);

  const shouldShowSpotlight =
    props.spotlightDeals &&
    props.spotlightDeals.length >= SPOTLIGHT_DEALS_COUNT;

  return (
    <div className="pb-5 sm:pt-5">
      <div className="sm:reebelo-container">
        {shouldShowSpotlight && <Spotlight spotlightDeals={spotlightDeals} />}
      </div>
      <div className="reebelo-container">
        {/* Hot Deal Title */}
        <h1 className="text-lg font-semibold md:text-xl">Hot Deals</h1>
        <div className="mt-3">
          <CategoryFilter topCategories={props.topCategories} />
        </div>
        <div className="mt-6 flex">
          <div
            className={cn(
              'fixed top-0 z-50 h-full w-full overflow-auto bg-white transition-all duration-500 ease-in-out lg:relative lg:left-auto lg:z-10 lg:block lg:w-[30%] xl:w-[25%]',
              {
                'left-0': showFilter,
                '-left-[999%]': !showFilter,
              },
            )}
          >
            <div className="sticky top-0 z-40 -mt-5 mb-5 border-b border-gray-500 bg-gray-200 px-6 pb-3 pt-4 lg:mb-7 lg:hidden">
              <h2 className="text-xl font-bold text-gray-700 xs:text-2xl">
                {t`Filters`}
              </h2>
              <button
                onClick={() => {
                  setFilterIsOpen(false);
                }}
                type="button"
                className="btn-close absolute right-5 top-5 opacity-100 md:right-6 md:top-6 md:h-2 md:w-2 lg:hidden"
                aria-label="Close"
              ></button>
            </div>
            <div className="grid grid-cols-1 pb-[70px] xxs:pb-20 lg:pb-0">
              {numberOfFilters > 0 && (
                <FilterBy
                  priceRangeValue={{
                    min: props.priceRange.min,
                    max: props.priceRange.max,
                  }}
                  priceRange={priceRange}
                  selected={flattenSelectedVariants}
                  onPriceRangeRemove={onPriceRangeRemove}
                />
              )}
              <FilterSidebar
                options={props.options}
                query={query}
                priceRange={props.priceRange}
                qs={qs}
                onPriceRangeChange={onPriceRangeChange}
                haslastMinuteDeals={props.haslastMinuteDeals}
                hasBundleOffers={props.hasBundleOffers}
                id="default"
              />

              {showFilter && (
                <div className="fixed bottom-0 z-80 w-full bg-white px-5 py-2 shadow lg:hidden">
                  <CtaButton
                    onClick={() => {
                      setFilterIsOpen(false);
                    }}
                    type="button"
                    variant="dark"
                    className="w-full text-sm !uppercase xs:text-base"
                  >
                    {t`View Results`}
                  </CtaButton>
                </div>
              )}
            </div>
          </div>
          <div className="ml-0 w-full lg:ml-6 lg:w-[70%] xl:ml-10 xl:w-[75%]">
            <div className="flex justify-end xs:mt-0 lg:mb-4 lg:flex-row">
              <div className="ml-2 hidden w-[20%] min-w-[220px] lg:block">
                <SortSelect
                  shouldShowTopBar={shouldShowTopBar}
                  isScrolling={isScrolling}
                  handleDropdownOpen={handleDropdownOpen}
                  isDropDownOpen={dropdownOpen}
                  dropdownRef={dropdownRef}
                />
              </div>
              <div
                id="collectionMobileFilter"
                className={cn('flex w-full lg:hidden', {
                  'fixed left-0 top-[58px] z-[49] w-full bg-white px-7 py-4 drop-shadow-md xxs:top-[68px] sm:top-[88px] lg:drop-shadow-md':
                    shouldShowTopBar,
                })}
              >
                <div
                  className={cn('w-1/2', {
                    hidden: dropdownOpen,
                  })}
                >
                  <button
                    onClick={() => setFilterIsOpen(true)}
                    className="flex h-full w-full items-center justify-center rounded-l-full border-2 border-gray-700 py-3 text-xs font-bold uppercase xs:text-sm sm:py-2 md:text-base"
                  >
                    {t`Filters`}
                    {numberOfFilters > 0 ? ` (${numberOfFilters})` : ''}
                    <div className="ml-3 w-[15px] xs:ml-4 xs:w-[18px]">
                      <Image src={filterIcon} alt="filter" className="w-full" />
                    </div>
                  </button>
                </div>
                <div
                  className={cn('w-1/2', {
                    '!w-full': dropdownOpen,
                  })}
                >
                  <SortSelect
                    shouldShowTopBar={shouldShowTopBar}
                    isScrolling={isScrolling}
                    handleDropdownOpen={handleDropdownOpen}
                    isDropDownOpen={dropdownOpen}
                    dropdownRef={dropdownRef}
                  />
                </div>
              </div>
            </div>

            <InfiniteScrollSearchResults
              deals={deals}
              hasNextPage={hasNextPage}
              key={router.asPath}
              spotlightDeals={spotlightDeals}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
